/* eslint-disable react/jsx-props-no-spreading */
import styled from "@emotion/styled";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { media } from "@iventis/styles";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import React, { forwardRef, MutableRefObject, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Authorisation } from "@iventis/domain-model/model/authorisation";
import { v4 as uuid } from "uuid";
import { isValidUuid } from "@iventis/utilities";
import { CommentComponent } from "./comment";
import { CommentInput } from "./comment-input";
import { StyledInteractiveElement } from "./comment-list";
import { useCommentsService } from "./comments-service";
import { createComment } from "./create-comment-helpers";
import { SkeletonComment } from "./comment-skeleton";
import { addCommentImageToTextContent, parseCommentTextContent } from "./comment-text-content-helpers";
import { CommentImage } from "./comment-types";

export const CommentForm = forwardRef(
    (
        {
            selectedCommentId,
            handleViewAllClicked,
            isNewComment,
            className,
        }: {
            selectedCommentId: string;
            handleViewAllClicked: () => void;
            isNewComment: boolean;
            className?: string;
        },
        ref: MutableRefObject<HTMLDivElement>
    ) => {
        const translate = useIventisTranslate();

        // Api calls and data
        const queryClient = useQueryClient();

        const { resource, create, update, get, loggedInUser, cancel, delete: deleteComment, postCommentImage } = useCommentsService();
        const resourceId = resource.id;

        const { data: comment, isLoading: isCommentLoading } = useQuery(
            ["comment", selectedCommentId],
            async () => {
                const response = await get(selectedCommentId, resourceId);
                setEditing(response != null);
                return response ?? null;
            },
            {
                staleTime: Infinity,
                enabled: !isNewComment,
            }
        );

        // Local state

        const [saving, setSaving] = useState(false);

        const [editing, setEditing] = useState(comment != null);

        // Handlers
        const handleInputSave = async (commentTextContent: string, commentImages: CommentImage[]) => {
            setSaving(true);
            let textContent: string;
            if (comment != null) {
                // Update the comment and set it locally
                const commentImage = commentImages[0];
                const isExistingImage = isValidUuid(commentImage);
                // Check if we already have an image
                if (isExistingImage && typeof commentImage === "string") {
                    textContent = addCommentImageToTextContent(commentTextContent, [commentImage]);
                } else if (commentImage != null && !isExistingImage) {
                    // Check if we get a base64 image which is a new image
                    const assetId = uuid();
                    await postCommentImage(assetId, commentImage);
                    textContent = addCommentImageToTextContent(commentTextContent, [assetId]);
                } else if (!commentImage) {
                    // Check if we get comment with no image and set new comment text
                    textContent = commentTextContent;
                }
                if (textContent !== comment.textContent) {
                    await update({ ...comment, textContent, lastUpdatedByUserId: loggedInUser.id }, resourceId);
                    queryClient.setQueryData(["comment", selectedCommentId], { ...comment, textContent, lastUpdatedAt: new Date() });
                }
            } else {
                // Create the comment and set it locally
                textContent = commentTextContent;
                const commentImage = commentImages[0];
                if (commentImage != null) {
                    const assetId = uuid();
                    await postCommentImage(assetId, commentImage);
                    textContent = addCommentImageToTextContent(commentTextContent, [assetId]);
                }
                const createdComment = createComment({
                    id: selectedCommentId,
                    textContent,
                    resourceId,
                    userId: loggedInUser.id,
                    resourceType: resource.type,
                });
                queryClient.setQueryData(["comment", createdComment.id], createdComment);
                await create(createdComment, resourceId);
            }
            // Invalidate comments for the resource
            queryClient.invalidateQueries(["comments", resourceId]);
            setEditing(true);
            setSaving(false);
        };

        const handleCancelClicked = () => {
            if (comment != null) {
                // If the comment has already been saved just remove local changes
                setEditing(true);
            } else {
                // If the comment has not been saved just remove it
                cancel();
            }
        };

        const doesCommentHaveExistingImage = parseCommentTextContent(comment?.textContent)?.commentImageIds?.length > 0;

        return (
            <div className={className} ref={ref} data-testid="comment-container">
                {isCommentLoading && !isNewComment ? (
                    <SkeletonComment />
                ) : (
                    <>
                        <StyledMobileHeader>
                            <span>{translate(Content.comments.comment)}</span>

                            <StyledInteractiveElement onClick={() => (editing || isNewComment ? cancel() : setEditing(true))} data-testid="close-comment-button">
                                <FontAwesomeIcon icon={["far", "xmark"]} size="lg" />
                            </StyledInteractiveElement>
                        </StyledMobileHeader>
                        {editing ? (
                            <CommentComponent
                                {...comment}
                                userId={comment ? comment.userId : loggedInUser.id}
                                textContent={comment?.textContent}
                                handleViewAll={handleViewAllClicked}
                                handleEditClick={comment?.userId === loggedInUser.id ? () => setEditing(false) : undefined}
                                handleDeleteClick={
                                    comment?.userId === loggedInUser.id || loggedInUser.resourcePermission === Authorisation.Admin
                                        ? () => deleteComment(selectedCommentId, resourceId)
                                        : undefined
                                }
                                handleImageUpload={(base64Image) => handleInputSave(parseCommentTextContent(comment?.textContent).commentText, [base64Image])}
                                disableImageButton={doesCommentHaveExistingImage}
                            />
                        ) : (
                            <StyledCommentInput comment={comment?.textContent ?? ""} handleSave={handleInputSave} handleCancel={handleCancelClicked} saving={saving} />
                        )}
                    </>
                )}
            </div>
        );
    }
);

const StyledMobileHeader = styled.div`
    height: 44px;
    padding-left: 15px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid ${({ theme }) => theme.tertiaryColors.headerBackground};
    ${media.medium} {
        display: none;
    }
`;

const StyledCommentInput = styled(CommentInput)`
    box-sizing: border-box;
    margin: 15px;
    ${media.medium} {
        margin: 0;
    }
`;
